import { Link } from "react-router-dom";
import { ShoppingCart } from "lucide-react";
import { productDetailPath } from "../utils/productUrl";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEASON_LABELS = { summer: "Estivo", winter: "Invernale", all_season: "4 Stagioni" };
const SEASON_CLASSES = {
  summer: "badge-summer",
  winter: "badge-winter",
  all_season: "badge-all_season",
};

export default function ProductCard({ product, priority = false }) {
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.error("Accedi per aggiungere prodotti al carrello");
      return;
    }
    try {
      await addToCart(product.id);
      toast.success(`${product.name} aggiunto al carrello`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'aggiunta al carrello");
    }
  };

  // Thumbnail 280px per card (~260px display): riduce download ~161 KiB (Lighthouse)
  const imagePath = product.images?.[0];
  const imageUrl = imagePath
    ? `${API_URL}/api/thumb?path=${encodeURIComponent(imagePath)}&w=280`
    : null;

  const hasDiscount = product.discount_price && product.discount_price < product.price;

  return (
    <Link to={productDetailPath(product)} className="card group flex flex-col overflow-hidden rounded-2xl">
      <div className="relative aspect-square bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={product.name}
            width={260}
            height={260}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={priority ? "high" : undefined}
            className="w-full h-full object-contain p-4 group-hover:scale-[1.06] transition-transform duration-400 ease-out"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-200">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
              <circle cx="12" cy="12" r="10" />
              <circle cx="12" cy="12" r="3" />
              <path d="M12 2a10 10 0 0 1 0 20" strokeDasharray="2 2" />
            </svg>
          </div>
        )}
        <div className="absolute top-2.5 left-2.5 flex gap-1.5">
          <span className={`badge ${SEASON_CLASSES[product.season] || "bg-gray-100 text-gray-700"}`}>
            {SEASON_LABELS[product.season] || product.season}
          </span>
          {hasDiscount && (
            <span className="badge bg-pg-red text-pg-dark">
              -{Math.round((1 - product.discount_price / product.price) * 100)}%
            </span>
          )}
        </div>
        {product.stock <= 0 && (
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center">
            <span className="bg-white/90 text-gray-800 px-3 py-1 rounded-full text-xs font-bold tracking-wide shadow">Esaurito</span>
          </div>
        )}
      </div>

      <div className="p-4 flex flex-col flex-1">
        <p className="text-[11px] text-pg-gray font-semibold uppercase tracking-widest">{product.brand}</p>
        <h2 className="font-semibold text-gray-900 mt-1 text-sm leading-snug line-clamp-2 group-hover:text-pg-red transition-colors duration-150">
          {product.name}
        </h2>
        <p className="text-xs text-pg-gray mt-1 font-medium">{product.size}</p>

        <div className="mt-auto pt-3 flex items-center justify-between">
          <div className="flex flex-col">
            {hasDiscount ? (
              <>
                <span className="text-base font-black text-amber-700 leading-tight">{product.discount_price.toFixed(2)} &euro;</span>
                <span className="text-xs text-gray-500 line-through leading-tight">{product.price.toFixed(2)} &euro;</span>
              </>
            ) : (
              <span className="text-base font-black text-gray-900">{product.price.toFixed(2)} &euro;</span>
            )}
          </div>
          {product.stock > 0 && (
            <button
              onClick={handleAddToCart}
              className="p-2 rounded-xl bg-pg-red text-pg-dark hover:bg-pg-red-dark hover:shadow-md transition-all duration-150 hover:-translate-y-0.5 active:translate-y-0"
              title="Aggiungi al carrello"
            >
              <ShoppingCart size={17} />
            </button>
          )}
        </div>
      </div>
    </Link>
  );
}
